// Per-property guest reviews rendered inside the "About the Hotel" text
// column, right below the founder quote. Each property gets 3–4 curated
// testimonials keyed to its distinctive traits (kasbah, dunes, spa lake,
// riad, refuge, bivouac tier), never generic filler.
//
// Shape:
//   {
//     [hotelSlug]: [{
//       id,          // stable key
//       author,      // full name or initials
//       origin,      // { en, fr, es } — city / country
//       rating,      // integer 1-5
//       date,        // { en, fr, es } — short relative marker
//       quote,       // { en, fr, es } — 1-3 sentence review
//     }, ...]
//   }
//
// When a slug has no entry we fall back to the DEFAULT_REVIEWS array so
// every property still shows something on-brand.

const R = (id, author, origin, rating, date, quote) => ({
  id, author, origin, rating, date, quote,
});

const HOTEL_REVIEWS = {
  "kasbah-hotel-xaluca-arfoud": [
    R("kha-1", "Emma & Julien P.", { en: "Lyon, France", fr: "Lyon, France", es: "Lyon, Francia" }, 5,
      { en: "3 weeks ago", fr: "il y a 3 semaines", es: "hace 3 semanas" },
      {
        en: "The kasbah walls feel alive at dusk — pisé warmed by fifty years of sun. Rooftop dinner with the palm grove below is a memory we'll keep.",
        fr: "Les murs de la kasbah semblent vivants au crépuscule — pisé chauffé par cinquante ans de soleil. Dîner sur le toit face à la palmeraie : un souvenir précieux.",
        es: "Los muros de la kasbah cobran vida al atardecer — adobe caliente de cincuenta años de sol. Cena en la azotea frente al palmeral: un recuerdo inolvidable.",
      }),
    R("kha-2", "H. Nakamura", { en: "Kyoto, Japan", fr: "Kyoto, Japon", es: "Kyoto, Japón" }, 5,
      { en: "Last month", fr: "le mois dernier", es: "el mes pasado" },
      {
        en: "Arriving from Merzouga after a dune sunrise, the pool and the mint tea felt like a soft landing. Staff remembered our names by breakfast.",
        fr: "Après le lever du soleil sur les dunes de Merzouga, la piscine et le thé à la menthe furent un atterrissage tout en douceur. On se souvenait de nos prénoms dès le petit-déjeuner.",
        es: "Volviendo del amanecer en Merzouga, la piscina y el té con menta fueron el aterrizaje perfecto. Nos llamaban por el nombre desde el desayuno.",
      }),
    R("kha-3", "The Osborne family", { en: "Bristol, UK", fr: "Bristol, RU", es: "Bristol, RU" }, 5,
      { en: "2 months ago", fr: "il y a 2 mois", es: "hace 2 meses" },
      {
        en: "The kids ran barefoot through the pisé courtyards while we drank cardamom coffee. Rooms are enormous — hand-woven Berber rugs everywhere.",
        fr: "Les enfants couraient pieds nus dans les patios en pisé pendant qu'on prenait un café à la cardamome. Chambres immenses, tapis berbères tissés main.",
        es: "Los niños corrían descalzos por los patios de adobe mientras tomábamos café con cardamomo. Habitaciones enormes con alfombras bereberes tejidas a mano.",
      }),
    R("kha-4", "Sofía L.", { en: "Barcelona, Spain", fr: "Barcelone, Espagne", es: "Barcelona, España" }, 4,
      { en: "6 weeks ago", fr: "il y a 6 semaines", es: "hace 6 semanas" },
      {
        en: "Booked the spa at 9pm after a full-day tour. Two therapists, hammam scent I still can't identify, and a fossil-stone massage table. Bliss.",
        fr: "Spa réservé à 21h après une journée d'excursion. Deux thérapeutes, un parfum de hammam introuvable et une table en marbre fossile. Un bonheur.",
        es: "Reservé el spa a las 21h tras un tour completo. Dos terapeutas, un aroma de hammam que aún no identifico y mesa de mármol fósil. Puro placer.",
      }),
  ],

  "kasbah-hotel-tombouctou": [
    R("kht-1", "Marta R.", { en: "Madrid, Spain", fr: "Madrid, Espagne", es: "Madrid, España" }, 5,
      { en: "Last month", fr: "le mois dernier", es: "el mes pasado" },
      {
        en: "The pool wraps around a real palm island — the fresco of Tombouctou in the lobby is worth its own trip. Deep, honest hospitality.",
        fr: "La piscine entoure une véritable île de palmiers — la fresque de Tombouctou dans le lobby mérite le voyage. Hospitalité profonde et sincère.",
        es: "La piscina rodea una isla real de palmeras — el fresco de Tombouctou en el lobby merece el viaje. Hospitalidad profunda y auténtica.",
      }),
    R("kht-2", "Peter & Astrid H.", { en: "Vienna, Austria", fr: "Vienne, Autriche", es: "Viena, Austria" }, 5,
      { en: "3 weeks ago", fr: "il y a 3 semaines", es: "hace 3 semanas" },
      {
        en: "We arrived on caravan road and left convinced Rissani was our new favourite town. Rooftop breakfast facing the erg is unbeatable.",
        fr: "Arrivés par la route caravanière, repartis convaincus que Rissani est notre nouvelle ville favorite. Petit-déjeuner en terrasse face à l'erg : imbattable.",
        es: "Llegamos por la ruta caravanera y nos fuimos convencidos de que Rissani es nuestra ciudad favorita. Desayuno en la terraza frente al erg: imbatible.",
      }),
    R("kht-3", "J. Okafor", { en: "Lagos, Nigeria", fr: "Lagos, Nigéria", es: "Lagos, Nigeria" }, 4,
      { en: "2 months ago", fr: "il y a 2 mois", es: "hace 2 meses" },
      {
        en: "Excellent tagines, wonderful storyteller at dinner explaining the Tombouctou caravan history — genuine, not touristy.",
        fr: "Tajines excellents, conteur formidable au dîner sur l'histoire caravanière de Tombouctou — authentique, jamais touristique.",
        es: "Tajines excelentes, cuentacuentos en la cena narrando la ruta caravanera de Tombouctou — auténtico, nada turístico.",
      }),
  ],

  "hotel-xaluca-dades": [
    R("hxd-1", "Camille D.", { en: "Bordeaux, France", fr: "Bordeaux, France", es: "Burdeos, Francia" }, 5,
      { en: "Last week", fr: "la semaine dernière", es: "la semana pasada" },
      {
        en: "Every window frames the gorge — we watched the ochre cliffs turn violet from the bathtub. Excellent base for hiking the Monkey Fingers.",
        fr: "Chaque fenêtre encadre la gorge — nous avons vu les falaises ocres virer au violet depuis la baignoire. Base idéale pour randonner les Doigts de Singe.",
        es: "Cada ventana enmarca el desfiladero — vimos los acantilados ocres tornarse violeta desde la bañera. Base perfecta para caminar por los Dedos del Mono.",
      }),
    R("hxd-2", "T. Bergstrom", { en: "Stockholm, Sweden", fr: "Stockholm, Suède", es: "Estocolmo, Suecia" }, 5,
      { en: "2 weeks ago", fr: "il y a 2 semaines", es: "hace 2 semanas" },
      {
        en: "Cool mountain nights, warm rugs, a rooftop nightingale. Route from Merzouga to Ouarzazate is intense — this hotel restores you.",
        fr: "Nuits fraîches en montagne, tapis chauds, rossignol sur le toit. La route Merzouga–Ouarzazate est intense : cet hôtel vous répare.",
        es: "Noches frescas de montaña, alfombras cálidas, un ruiseñor en la azotea. La ruta Merzouga–Ouarzazate es intensa: este hotel te repara.",
      }),
    R("hxd-3", "Anne-Sophie M.", { en: "Geneva, Switzerland", fr: "Genève, Suisse", es: "Ginebra, Suiza" }, 5,
      { en: "1 month ago", fr: "il y a 1 mois", es: "hace 1 mes" },
      {
        en: "The chef came out to explain the amlou at breakfast. Little touches like that turn a stopover into a proper stay.",
        fr: "Le chef est venu expliquer l'amlou au petit-déjeuner. Ce genre d'attention transforme une étape en vrai séjour.",
        es: "El chef salió a explicarnos el amlou en el desayuno. Detalles así convierten una escala en una verdadera estancia.",
      }),
    R("hxd-4", "M. Chen", { en: "Singapore", fr: "Singapour", es: "Singapur" }, 4,
      { en: "5 weeks ago", fr: "il y a 5 semaines", es: "hace 5 semanas" },
      {
        en: "Booked as a 1-night stop, extended to 3. The nomad-tent rooftop lounge at sunset with a mint tea = pure Morocco.",
        fr: "Réservé pour 1 nuit, prolongé à 3. Le salon-tente nomade sur le toit au coucher du soleil avec un thé à la menthe = le vrai Maroc.",
        es: "Reservé 1 noche, extendí a 3. El salón-jaima en la azotea al atardecer con té de menta = Marruecos en estado puro.",
      }),
  ],

  "bivouacs-xaluca": [
    R("bx-1", "R. & K. Nováková", { en: "Prague, Czechia", fr: "Prague, Tchéquie", es: "Praga, Chequia" }, 5,
      { en: "3 weeks ago", fr: "il y a 3 semaines", es: "hace 3 semanas" },
      {
        en: "Silent camel arrival, warm bread already on the low table. The Gnawa musicians came at 10pm — we danced barefoot in the sand.",
        fr: "Arrivée à dos de chameau dans le silence, pain chaud déjà sur la table basse. Les musiciens Gnawa sont venus à 22h — nous avons dansé pieds nus dans le sable.",
        es: "Llegada silenciosa en camello, pan caliente ya en la mesa baja. Los músicos gnawa vinieron a las 22h — bailamos descalzos en la arena.",
      }),
    R("bx-2", "Diego F.", { en: "Buenos Aires, Argentina", fr: "Buenos Aires, Argentine", es: "Buenos Aires, Argentina" }, 5,
      { en: "Last month", fr: "le mois dernier", es: "el mes pasado" },
      {
        en: "The Milky Way was so bright it woke me up. Guide climbed the dune with a thermos of coffee at sunrise. This is the desert done right.",
        fr: "La Voie lactée m'a réveillé tellement elle brillait. Le guide a grimpé la dune avec un thermos de café au lever du soleil. Voilà le désert comme il se doit.",
        es: "La Vía Láctea era tan brillante que me despertó. El guía subió la duna con un termo de café al amanecer. El desierto como debe ser.",
      }),
    R("bx-3", "L. van der Berg", { en: "Amsterdam, Netherlands", fr: "Amsterdam, Pays-Bas", es: "Ámsterdam, Países Bajos" }, 5,
      { en: "2 months ago", fr: "il y a 2 mois", es: "hace 2 meses" },
      {
        en: "Ensuite hot shower under canvas — didn't expect that in the Sahara. Camel driver Hassan is a legend, still texting us photos.",
        fr: "Douche chaude privée sous la toile — inattendu au Sahara. Notre chamelier Hassan est une légende, il nous envoie encore des photos.",
        es: "Ducha caliente propia bajo lona — inesperado en el Sáhara. Nuestro guía Hassan es una leyenda, aún nos manda fotos.",
      }),
  ],

  "hotel-riad-xaluca": [
    R("hrx-1", "Isabelle T.", { en: "Nantes, France", fr: "Nantes, France", es: "Nantes, Francia" }, 5,
      { en: "Last week", fr: "la semaine dernière", es: "la semana pasada" },
      {
        en: "The riad's courtyard fountain hums all night. Only 12 rooms — we felt like private guests, not customers. Zellige everywhere.",
        fr: "La fontaine du patio du riad murmure toute la nuit. Seulement 12 chambres — invités privés, jamais clients. Zellige partout.",
        es: "La fuente del patio del riad murmura toda la noche. Solo 12 habitaciones — invitados privados, no clientes. Zellige por doquier.",
      }),
    R("hrx-2", "K. Wojcik", { en: "Warsaw, Poland", fr: "Varsovie, Pologne", es: "Varsovia, Polonia" }, 5,
      { en: "1 month ago", fr: "il y a 1 mois", es: "hace 1 mes" },
      {
        en: "Cook Fatima invited us to help her fold the pastilla dough. This kind of intimacy is impossible in a bigger hotel.",
        fr: "La cuisinière Fatima nous a invités à plier la pâte de la pastilla. Cette intimité est impossible dans un plus grand hôtel.",
        es: "La cocinera Fatima nos invitó a doblar la masa de la pastilla. Esa intimidad es imposible en un hotel más grande.",
      }),
    R("hrx-3", "Andrew L.", { en: "Melbourne, Australia", fr: "Melbourne, Australie", es: "Melbourne, Australia" }, 4,
      { en: "6 weeks ago", fr: "il y a 6 semaines", es: "hace 6 semanas" },
      {
        en: "Rooftop plunge pool at dawn, birds only. The best sunrise breakfast I've had in Morocco — and I've had a few.",
        fr: "Piscine de toit à l'aube, uniquement les oiseaux. Meilleur petit-déjeuner au lever du soleil au Maroc — et j'en ai eu quelques-uns.",
        es: "Piscina en la azotea al amanecer, solo pájaros. El mejor desayuno al alba de Marruecos — y he visto varios.",
      }),
  ],

  "hotel-xaluca-spa-aguelmame-sidi-ali": [
    R("hxs-1", "Nathalie B.", { en: "Rennes, France", fr: "Rennes, France", es: "Rennes, Francia" }, 5,
      { en: "3 weeks ago", fr: "il y a 3 semaines", es: "hace 3 semanas" },
      {
        en: "Wake up to Barbary macaques in the cedar forest, breakfast facing the sacred lake. The spa treatments use plants from the crater.",
        fr: "Réveil avec les macaques dans la cédraie, petit-déjeuner face au lac sacré. Les soins spa utilisent des plantes du cratère.",
        es: "Despertar con macacos en el cedral, desayuno frente al lago sagrado. Los tratamientos del spa usan plantas del cráter.",
      }),
    R("hxs-2", "The Kellerman family", { en: "Munich, Germany", fr: "Munich, Allemagne", es: "Múnich, Alemania" }, 5,
      { en: "Last month", fr: "le mois dernier", es: "el mes pasado" },
      {
        en: "Middle Atlas air is a revelation after a week of desert heat. Fireplace room, wool blankets, tagine on a wood stove. Perfect.",
        fr: "L'air du Moyen Atlas est une révélation après une semaine de chaleur désertique. Chambre avec cheminée, tajine sur poêle à bois. Parfait.",
        es: "El aire del Atlas Medio es un descubrimiento tras una semana de calor del desierto. Habitación con chimenea, tajín en fogón de leña. Perfecto.",
      }),
    R("hxs-3", "Yuki S.", { en: "Tokyo, Japan", fr: "Tokyo, Japon", es: "Tokio, Japón" }, 5,
      { en: "2 months ago", fr: "il y a 2 mois", es: "hace 2 meses" },
      {
        en: "Booked for the lake, stayed for the hammam. Argan-oil massage after a hike around Aguelmame — I could feel the altitude leaving my body.",
        fr: "Réservé pour le lac, resté pour le hammam. Massage à l'huile d'argan après une balade autour d'Aguelmame — l'altitude quittait mon corps.",
        es: "Reservé por el lago, me quedé por el hammam. Masaje de argán tras un paseo por Aguelmame — sentí cómo la altitud dejaba mi cuerpo.",
      }),
  ],

  "xaluca-ifess-fess-refuge": [
    R("xif-1", "P. Rossi", { en: "Milan, Italy", fr: "Milan, Italie", es: "Milán, Italia" }, 5,
      { en: "2 weeks ago", fr: "il y a 2 semaines", es: "hace 2 semanas" },
      {
        en: "Perfect stopover between Fes and the desert. Small, warm, honest — mountain silence at night broken only by the wind on the cedars.",
        fr: "Étape parfaite entre Fès et le désert. Petit, chaleureux, sincère — silence de montagne la nuit, seul le vent dans les cèdres.",
        es: "Escala perfecta entre Fez y el desierto. Pequeño, cálido, sincero — silencio de montaña por la noche, solo el viento en los cedros.",
      }),
    R("xif-2", "S. Perreault", { en: "Montréal, Canada", fr: "Montréal, Canada", es: "Montreal, Canadá" }, 5,
      { en: "1 month ago", fr: "il y a 1 mois", es: "hace 1 mes" },
      {
        en: "Hiked to the Barbary macaque forest with the refuge guide — a highlight of our whole trip. Return: hot lentil soup by the fire.",
        fr: "Randonnée jusqu'à la forêt des macaques avec le guide du refuge — le point fort du séjour. Retour : soupe de lentilles au coin du feu.",
        es: "Caminata al bosque de macacos con el guía del refugio — lo mejor del viaje. Regreso: sopa de lentejas junto al fuego.",
      }),
    R("xif-3", "H. Al-Sayed", { en: "Dubai, UAE", fr: "Dubaï, EAU", es: "Dubái, EAU" }, 4,
      { en: "5 weeks ago", fr: "il y a 5 semaines", es: "hace 5 semanas" },
      {
        en: "Simple rooms, spectacular views. This is a refuge, not a resort — that's exactly why it works. Ferrat, our host, was outstanding.",
        fr: "Chambres simples, vues spectaculaires. C'est un refuge, pas un resort — c'est précisément pourquoi cela fonctionne. Ferrat, notre hôte, était exceptionnel.",
        es: "Habitaciones sencillas, vistas espectaculares. Es un refugio, no un resort — por eso funciona. Ferrat, el anfitrión, brillante.",
      }),
  ],

  "auberge-la-belle-etoile": [
    R("alb-1", "F. Dupont", { en: "Toulouse, France", fr: "Toulouse, France", es: "Toulouse, Francia" }, 5,
      { en: "Last week", fr: "la semaine dernière", es: "la semana pasada" },
      {
        en: "Dinner served under an open sky, the Milky Way a full ceiling. No electricity glow for miles. Slept beneath 100,000 stars.",
        fr: "Dîner servi à ciel ouvert, la Voie lactée en guise de plafond. Aucun halo électrique alentour. Dormi sous 100 000 étoiles.",
        es: "Cena bajo cielo abierto, la Vía Láctea de techo. Ni una luz eléctrica en kilómetros. Dormimos bajo 100.000 estrellas.",
      }),
    R("alb-2", "The Vermeer family", { en: "Rotterdam, Netherlands", fr: "Rotterdam, Pays-Bas", es: "Róterdam, Países Bajos" }, 5,
      { en: "3 weeks ago", fr: "il y a 3 semaines", es: "hace 3 semanas" },
      {
        en: "The auberge feels like a private farm. Wood-fired bread every morning, the goats greet the kids at breakfast — magic.",
        fr: "L'auberge ressemble à une ferme privée. Pain au feu de bois chaque matin, les chèvres saluent les enfants au petit-déjeuner — magique.",
        es: "La posada parece una granja privada. Pan de horno de leña cada mañana, las cabras saludan a los niños en el desayuno — mágico.",
      }),
    R("alb-3", "J. Fernández", { en: "Sevilla, Spain", fr: "Séville, Espagne", es: "Sevilla, España" }, 5,
      { en: "2 months ago", fr: "il y a 2 mois", es: "hace 2 meses" },
      {
        en: "The astronomer host set up a telescope on the terrace — we saw Saturn's rings. Rooms rustic-warm, tea always ready.",
        fr: "L'hôte astronome a installé un télescope sur la terrasse — nous avons vu les anneaux de Saturne. Chambres rustiques et chaleureuses.",
        es: "El anfitrión astrónomo montó un telescopio en la terraza — vimos los anillos de Saturno. Habitaciones rústicas y cálidas.",
      }),
  ],

  "xaluca-ait-ben-haddou": [
    R("xab-1", "C. Bianchi", { en: "Bologna, Italy", fr: "Bologne, Italie", es: "Bolonia, Italia" }, 5,
      { en: "2 weeks ago", fr: "il y a 2 semaines", es: "hace 2 semanas" },
      {
        en: "Waking up with the UNESCO kasbah at eye-level from the terrace is unreal. Cross the river at dawn, come back to fresh msemen.",
        fr: "Se réveiller avec la kasbah UNESCO au niveau des yeux depuis la terrasse est irréel. Traverser la rivière à l'aube, revenir pour un msemen chaud.",
        es: "Despertar con la kasbah UNESCO a la altura de la vista desde la terraza es irreal. Cruzar el río al alba, volver a un msemen recién hecho.",
      }),
    R("xab-2", "T. & M. Adebayo", { en: "London, UK", fr: "Londres, RU", es: "Londres, RU" }, 5,
      { en: "1 month ago", fr: "il y a 1 mois", es: "hace 1 mes" },
      {
        en: "The host arranged a private tour with the head Amazigh guide of Aït Ben Haddou. That alone was worth the trip — we saw doors no tourist sees.",
        fr: "L'hôte a organisé une visite privée avec le guide amazigh principal d'Aït Ben Haddou. Cela valait le voyage — des portes que les touristes ne voient jamais.",
        es: "El anfitrión organizó una visita privada con el guía amazigh principal de Aït Ben Haddou. Solo eso valió el viaje — puertas que ningún turista ve.",
      }),
    R("xab-3", "N. Silva", { en: "Lisboa, Portugal", fr: "Lisbonne, Portugal", es: "Lisboa, Portugal" }, 4,
      { en: "6 weeks ago", fr: "il y a 6 semaines", es: "hace 6 semanas" },
      {
        en: "Gladiator was filmed 200m from our balcony. The hotel embraces the history without kitsch — proper local decor, no plastic.",
        fr: "Gladiator a été tourné à 200 m de notre balcon. L'hôtel embrasse l'histoire sans kitsch — décor local authentique, pas de plastique.",
        es: "Gladiator se rodó a 200 m de nuestro balcón. El hotel abraza la historia sin caer en el kitsch — decoración local auténtica.",
      }),
  ],

  "hotel-xaluca-midelt": [
    R("hxm-1", "L. Meyer", { en: "Strasbourg, France", fr: "Strasbourg, France", es: "Estrasburgo, Francia" }, 5,
      { en: "3 weeks ago", fr: "il y a 3 semaines", es: "hace 3 semanas" },
      {
        en: "Midelt is the doorway to the high Atlas — snow-capped peaks visible from the pool in April. Apple orchards behind the hotel.",
        fr: "Midelt, porte du Haut Atlas — sommets enneigés visibles depuis la piscine en avril. Vergers de pommiers derrière l'hôtel.",
        es: "Midelt es la puerta del Alto Atlas — cimas nevadas visibles desde la piscina en abril. Manzanales detrás del hotel.",
      }),
    R("hxm-2", "R. Cohen", { en: "Tel Aviv, Israel", fr: "Tel-Aviv, Israël", es: "Tel Aviv, Israel" }, 5,
      { en: "Last month", fr: "le mois dernier", es: "el mes pasado" },
      {
        en: "Perfect mid-stop between Fez and Merzouga. Vast, quiet rooms, honest local food. The staff played hidden-gem music on request.",
        fr: "Étape parfaite entre Fès et Merzouga. Chambres vastes et silencieuses, cuisine locale honnête. Le personnel a joué une musique rare sur demande.",
        es: "Escala perfecta entre Fez y Merzouga. Habitaciones amplias y tranquilas, cocina local honesta. El personal puso música rara si se pedía.",
      }),
    R("hxm-3", "The Andersson family", { en: "Oslo, Norway", fr: "Oslo, Norvège", es: "Oslo, Noruega" }, 4,
      { en: "2 months ago", fr: "il y a 2 mois", es: "hace 2 meses" },
      {
        en: "Kids loved the ping-pong in the courtyard. Adults loved the mineral shop across the street — Midelt is famous for its fossils.",
        fr: "Les enfants ont adoré le ping-pong dans la cour. Les adultes, la boutique de minéraux en face — Midelt est réputée pour ses fossiles.",
        es: "A los niños les encantó el ping-pong en el patio. A los adultos, la tienda de minerales enfrente — Midelt es famosa por sus fósiles.",
      }),
  ],

  "hotel-xaluca-dunes": [
    R("hxdu-1", "V. Petrov", { en: "Sofia, Bulgaria", fr: "Sofia, Bulgarie", es: "Sofía, Bulgaria" }, 5,
      { en: "2 weeks ago", fr: "il y a 2 semaines", es: "hace 2 semanas" },
      {
        en: "The dunes literally start behind the pool. Camel departure directly from our room's door — no minibus, no transfer.",
        fr: "Les dunes commencent littéralement derrière la piscine. Départ chamelier directement à la porte de la chambre — pas de minibus.",
        es: "Las dunas empiezan literalmente detrás de la piscina. Salida en camello directamente en la puerta de la habitación — sin traslado.",
      }),
    R("hxdu-2", "M. & S. O'Connor", { en: "Dublin, Ireland", fr: "Dublin, Irlande", es: "Dublín, Irlanda" }, 5,
      { en: "Last month", fr: "le mois dernier", es: "el mes pasado" },
      {
        en: "Golden hour on the terrace is otherworldly — Erg Chebbi turns red-copper. Best mint tea we had in the whole country.",
        fr: "L'heure dorée sur la terrasse est irréelle — l'Erg Chebbi devient rouge cuivré. Meilleur thé à la menthe de tout le pays.",
        es: "La hora dorada en la terraza es de otro mundo — Erg Chebbi se tiñe de rojo cobrizo. El mejor té con menta que probamos en todo el país.",
      }),
    R("hxdu-3", "A. Nakamura", { en: "Osaka, Japan", fr: "Osaka, Japon", es: "Osaka, Japón" }, 5,
      { en: "6 weeks ago", fr: "il y a 6 semaines", es: "hace 6 semanas" },
      {
        en: "Booked the Deluxe with dune-facing terrace — worth every dirham. Watched the sunrise wrapped in a Berber blanket, still in bed.",
        fr: "Chambre Deluxe avec terrasse face aux dunes — chaque dirham valait la peine. Lever de soleil enveloppé dans une couverture berbère, au lit.",
        es: "Reservé la Deluxe con terraza a las dunas — cada dírham valió la pena. Vi el amanecer envuelto en una manta bereber, aún en la cama.",
      }),
  ],

  "la-belle-etoile-bivouacs": [
    R("lbeb-1", "É. Laurent", { en: "Paris, France", fr: "Paris, France", es: "París, Francia" }, 5,
      { en: "Last week", fr: "la semaine dernière", es: "la semana pasada" },
      {
        en: "Chose the Nomad-Luxe tier — private terrace inside the tent, real bed with linen sheets, hot ensuite shower under the stars.",
        fr: "Nous avons choisi la formule Nomade-Luxe — terrasse privée dans la tente, vrai lit en lin, douche chaude privée sous les étoiles.",
        es: "Elegimos la categoría Nomad-Luxe — terraza privada en la jaima, cama real con sábanas de lino, ducha caliente propia bajo las estrellas.",
      }),
    R("lbeb-2", "K. Andersson", { en: "Copenhagen, Denmark", fr: "Copenhague, Danemark", es: "Copenhague, Dinamarca" }, 5,
      { en: "2 weeks ago", fr: "il y a 2 semaines", es: "hace 2 semanas" },
      {
        en: "The Signature Camp is an entire experience — sunset apéritif, drum circle under an oud player, four-course tagine dinner. Unforgettable.",
        fr: "Le Signature Camp est une expérience complète — apéritif au coucher du soleil, cercle de tambours avec un joueur d'oud, dîner tajine 4 services. Inoubliable.",
        es: "El Signature Camp es toda una experiencia — aperitivo al atardecer, círculo de tambores con un intérprete de oud, cena de tajín en 4 tiempos. Inolvidable.",
      }),
    R("lbeb-3", "S. Rodrigues", { en: "São Paulo, Brazil", fr: "São Paulo, Brésil", es: "São Paulo, Brasil" }, 5,
      { en: "1 month ago", fr: "il y a 1 mois", es: "hace 1 mes" },
      {
        en: "We took the Wild-Nomad tier — closest to how caravans slept. Simple canvas, wool rugs, and a ridiculous starfield. Perfect.",
        fr: "Nous avons pris la formule Nomade-Sauvage — au plus près des caravanes d'autrefois. Toile simple, tapis de laine et un ciel étoilé ridicule. Parfait.",
        es: "Elegimos la categoría Nomad Salvaje — lo más parecido a cómo dormían las caravanas. Lona sencilla, alfombras de lana y un cielo estrellado ridículo. Perfecto.",
      }),
  ],

  "kamkamia-luxury-desert-camp": [
    R("klc-1", "R. Kaufman", { en: "Zurich, Switzerland", fr: "Zurich, Suisse", es: "Zúrich, Suiza" }, 5,
      { en: "Last month", fr: "le mois dernier", es: "el mes pasado" },
      {
        en: "This is glamping at the highest level: king bed inside a Bedouin tent, en-suite marble bathroom, private butler. It doesn't get better.",
        fr: "Glamping au plus haut niveau : lit king dans une tente bédouine, salle de bain marbre en suite, majordome privé. On ne fait pas mieux.",
        es: "Glamping al más alto nivel: cama king en jaima beduina, baño de mármol en suite, mayordomo privado. Imposible mejorar.",
      }),
    R("klc-2", "H. Yoon", { en: "Seoul, South Korea", fr: "Séoul, Corée du Sud", es: "Seúl, Corea del Sur" }, 5,
      { en: "3 weeks ago", fr: "il y a 3 semaines", es: "hace 3 semanas" },
      {
        en: "The private dune of Kamkamia is a proper wow moment — no other lights on the horizon. Chef prepared a mechoui for our anniversary.",
        fr: "La dune privée de Kamkamia est un vrai moment 'wow' — aucune autre lumière à l'horizon. Le chef a préparé un méchoui pour notre anniversaire.",
        es: "La duna privada de Kamkamia es un momento 'wow' auténtico — ni una luz más en el horizonte. El chef nos preparó un mechoui por nuestro aniversario.",
      }),
    R("klc-3", "L. Bellucci", { en: "Rome, Italy", fr: "Rome, Italie", es: "Roma, Italia" }, 5,
      { en: "5 weeks ago", fr: "il y a 5 semaines", es: "hace 5 semanas" },
      {
        en: "Sandboarding at dawn, hot hammam in a tent at noon, oud recital at night. Every hour was scripted like an opera. Bravissimo.",
        fr: "Sandboard à l'aube, hammam chaud sous tente à midi, récital d'oud le soir. Chaque heure orchestrée comme un opéra. Bravissimo.",
        es: "Sandboard al alba, hammam bajo lona al mediodía, recital de oud por la noche. Cada hora orquestada como una ópera. Bravissimo.",
      }),
  ],

  "merdani-bivouac": [
    R("mb-1", "C. Weiss", { en: "Berlin, Germany", fr: "Berlin, Allemagne", es: "Berlín, Alemania" }, 5,
      { en: "2 weeks ago", fr: "il y a 2 semaines", es: "hace 2 semanas" },
      {
        en: "Merdani is remote — no other camp in sight. Zero light pollution: I actually saw the Andromeda galaxy with my naked eyes.",
        fr: "Merdani est reculé — aucun autre camp en vue. Pollution lumineuse zéro : j'ai vu la galaxie d'Andromède à l'œil nu.",
        es: "Merdani es remoto — ni un campamento a la vista. Cero contaminación lumínica: vi la galaxia de Andrómeda a simple vista.",
      }),
    R("mb-2", "L. Fischer", { en: "Vienna, Austria", fr: "Vienne, Autriche", es: "Viena, Austria" }, 5,
      { en: "Last month", fr: "le mois dernier", es: "el mes pasado" },
      {
        en: "The 4×4 track to Merdani is part of the fun — dune surfing on the way in. Once you arrive, everything slows down to Berber time.",
        fr: "La piste 4×4 vers Merdani fait partie du plaisir — surf sur dune à l'aller. Une fois arrivés, tout ralentit au rythme berbère.",
        es: "La pista 4×4 hasta Merdani ya es divertida — surf en duna en el trayecto. Al llegar, todo se ralentiza al ritmo bereber.",
      }),
    R("mb-3", "S. Petit", { en: "Nice, France", fr: "Nice, France", es: "Niza, Francia" }, 4,
      { en: "1 month ago", fr: "il y a 1 mois", es: "hace 1 mes" },
      {
        en: "Intimate — only 6 tents. Our host played the guembri after dinner and told stories about the Blue People of the Sahara.",
        fr: "Intime — 6 tentes seulement. Notre hôte a joué du guembri après le dîner et raconté les Hommes Bleus du Sahara.",
        es: "Íntimo — solo 6 jaimas. Nuestro anfitrión tocó el guembri tras la cena y contó historias del Pueblo Azul del Sáhara.",
      }),
  ],
};

// Universal fallback for any hotel slug not explicitly listed. Kept
// deliberately generic so it never contradicts the property's actual
// features, but still on-brand.
const DEFAULT_REVIEWS = [
  R("def-1", "Guest of Grup Xaluca", { en: "Europe", fr: "Europe", es: "Europa" }, 5,
    { en: "Recently", fr: "Récemment", es: "Recientemente" },
    {
      en: "Warm service, honest local food, and the kind of quiet that only Southern Morocco knows how to give.",
      fr: "Service chaleureux, cuisine locale sincère et le silence unique du sud marocain.",
      es: "Servicio cálido, cocina local honesta y el silencio que solo el sur de Marruecos sabe regalar.",
    }),
  R("def-2", "Guest of Grup Xaluca", { en: "Europe", fr: "Europe", es: "Europa" }, 5,
    { en: "Recently", fr: "Récemment", es: "Recientemente" },
    {
      en: "The staff turn a hotel into a home — this is what makes Grup Xaluca different across every property.",
      fr: "Le personnel transforme un hôtel en maison — c'est ce qui rend Grup Xaluca différent partout.",
      es: "El personal convierte un hotel en un hogar — eso hace único a Grup Xaluca en cada propiedad.",
    }),
];

export const getHotelReviews = (slug) => HOTEL_REVIEWS[slug] || DEFAULT_REVIEWS;

export default HOTEL_REVIEWS;
